// src/pages/TraineePerformanceReportPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, getDocs, orderBy, Timestamp, where } from 'firebase/firestore';
import { intervalToDuration } from 'date-fns';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/TeamPerformancePage.module.css'; // Using the same CSS module for similar layout

// --- Helper Function Definitions (Ensure these are robust and ideally in a shared utils file) ---
const parseDateSafely = (dateValue) => {
    if (!dateValue) return null;
    try {
        if (dateValue instanceof Timestamp) { return dateValue.toDate(); }
        if (typeof dateValue === 'string') {
            const dateString = dateValue.length === 10 && !dateValue.includes('T') ? dateValue + 'T00:00:00Z' : dateValue;
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                const parts = dateValue.split('-');
                if (parts.length === 3) {
                    const year = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const day = parseInt(parts[2], 10);
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                        const utcDate = new Date(Date.UTC(year, month, day));
                         if (!isNaN(utcDate.getTime())) return utcDate;
                    }
                }
                console.warn("Invalid date string for parseDateSafely:", dateValue); return null;
            }
            return date;
        }
        if (dateValue instanceof Date && !isNaN(dateValue)) { return dateValue; }
        console.warn("Unknown or invalid date format for parseDateSafely:", dateValue); return null;
    } catch (error) { console.error("Error parsing date in parseDateSafely:", error, dateValue); return null; }
};

const isTaskOverdueForTrainee = (task) => {
    const today = new Date(); today.setHours(23, 59, 59, 999);
    const dueDate = parseDateSafely(task.endDate);
    const statusLower = task.status?.toLowerCase();
    if (!dueDate || statusLower === 'completed' || statusLower === 'cancelled') {
        return false;
    }
    return dueDate < today;
};

const isDateInRange = (dateFieldValue, rangeStart, rangeEnd) => {
    if (rangeStart === null && rangeEnd === null) return !!dateFieldValue;
    if (!dateFieldValue) return false;
    const dateToTest = parseDateSafely(dateFieldValue);
    if (!dateToTest) return false;
    let inRange = true;
    if (rangeStart && dateToTest < rangeStart) inRange = false;
    if (rangeEnd && dateToTest > rangeEnd) inRange = false;
    return inRange;
};

const formatAverageDuration = (averageMilliseconds) => {
    if (averageMilliseconds === null || averageMilliseconds < 0 || isNaN(averageMilliseconds)) return 'N/A';
    if (averageMilliseconds === 0) return 'Instantly';
    const endMs = Math.max(0, averageMilliseconds);
    const duration = intervalToDuration({ start: 0, end: endMs });
    const parts = [];
    if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
    if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
    if (parts.length < 2 && duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}m`);
    if (parts.length === 0) {
        if (duration.seconds && duration.seconds > 0) {
            parts.push(`${duration.seconds}s`);
        } else if (averageMilliseconds > 0) {
             return '< 1s';
        } else { return '0s'; }
    }
    if (parts.length === 0 && averageMilliseconds > 0) return '< 1m';
    if (parts.length === 0) return 'N/A';
    return parts.slice(0, 2).join(' ');
};
// --- End Helper Function Definitions ---

const managerAccessRoles = ['Manager', 'Accounts Manager'];


const TraineePerformanceReportPage = () => {
    const { currentUser, userRole, loading: authLoading } = useAuth();
    const [allTasks, setAllTasks] = useState([]);
    const [allTrainees, setAllTrainees] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');

    const [achievementsDateFilter, setAchievementsDateFilter] = useState('today');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!currentUser) {
            if (isMounted.current) {
                setError("Authentication required to view this page.");
                setLoadingData(false);
            }
            return;
        }
        if (userRole && !managerAccessRoles.includes(userRole)) {
            if (isMounted.current) {
                setError("Access Denied: This page is for managers only.");
                setLoadingData(false);
            }
            return;
        }

        const fetchData = async () => {
            if (!isMounted.current) return;
            setLoadingData(true);
            setError('');
            try {
                const usersCol = collection(db, 'users');
                const usersQuery = query(usersCol, where('role', '==', 'Trainee'), orderBy('name', 'asc'));
                const usersSnapshot = await getDocs(usersQuery);
                if (!isMounted.current) return;
                const fetchedTrainees = usersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || doc.data().email,
                    role: doc.data().role, 
                }));
                setAllTrainees(fetchedTrainees);

                const tasksCol = collection(db, 'tasks');
                const tasksQuery = query(tasksCol, orderBy('createdAt', 'desc'));
                const tasksSnapshot = await getDocs(tasksQuery);
                if (!isMounted.current) return;
                const fetchedTasks = tasksSnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id, ...data,
                        startDate: parseDateSafely(data.startDate),
                        endDate: parseDateSafely(data.endDate),
                        createdAt: parseDateSafely(data.createdAt),
                        updatedAt: parseDateSafely(data.updatedAt),
                        completedAt: parseDateSafely(data.completedAt),
                        cancelledAt: parseDateSafely(data.cancelledAt),
                        acknowledgedBy: data.acknowledgedBy || {},
                        assignedToUid: data.assignedToUid || (Array.isArray(data.assignedTo) && data.assignedTo.length === 1 ? data.assignedTo[0] : null)
                    };
                });
                setAllTasks(fetchedTasks);

            } catch (err) {
                console.error("Error fetching team performance data:", err);
                if (isMounted.current) setError("Failed to load team performance data. Check console for Firebase index errors.");
            } finally {
                if (isMounted.current) setLoadingData(false);
            }
        };
        fetchData();
    }, [currentUser, userRole, authLoading]);

    const achievementDateRange = useMemo(() => {
        const today = new Date();
        let start = new Date();
        let end = new Date();
        end.setHours(23, 59, 59, 999);

        switch (achievementsDateFilter) {
            case 'today': start.setHours(0, 0, 0, 0); break;
            case 'yesterday':
                start = new Date(today); start.setDate(today.getDate() - 1); start.setHours(0, 0, 0, 0);
                end = new Date(today); end.setDate(today.getDate() - 1); end.setHours(23, 59, 59, 999);
                break;
            case 'last7':
                start = new Date(today); start.setDate(today.getDate() - 6); start.setHours(0, 0, 0, 0);
                break;
            case 'last28':
                start = new Date(today); start.setDate(today.getDate() - 27); start.setHours(0, 0, 0, 0);
                break;
            case 'custom':
                if (customStartDate && customEndDate) {
                    start = parseDateSafely(customStartDate + "T00:00:00");
                    end = parseDateSafely(customEndDate + "T23:59:59.999");
                    if (!start || !end || end < start) return { start: null, end: null, isValid: false, filterType: 'custom' };
                } else return { start: null, end: null, isValid: false, filterType: 'custom' };
                break;
            case 'maximum': default: return { start: null, end: null, isValid: true, filterType: 'maximum' };
        }
        return { start, end, isValid: true, filterType: achievementsDateFilter };
    }, [achievementsDateFilter, customStartDate, customEndDate]);

    const traineePerformanceStats = useMemo(() => {
        if (loadingData || !allTasks.length || !allTrainees.length || (achievementsDateFilter === 'custom' && !achievementDateRange.isValid)) {
            return [];
        }
        const { start: rangeStart, end: rangeEnd, filterType } = achievementDateRange;
        
        return allTrainees.map(trainee => {
            let stats = {
                activeTasks: 0, submittedInRange: 0, completedInRange: 0,
                needsRevisionInRange: 0, overdueInRange: 0,
                totalAckTime: 0, ackTasksCount: 0,
                totalWorkCycleTime: 0, submittedTasksForCycleCount: 0,
                performanceScore: 0 // Initialize performance score
            };

            const traineeTasks = allTasks.filter(task => task.assignedToUid === trainee.id );

            traineeTasks.forEach(task => {
                const createdAtDate = parseDateSafely(task.createdAt);
                const acknowledgedAtDate = task.acknowledgedBy && task.acknowledgedBy[trainee.id] ? parseDateSafely(task.acknowledgedBy[trainee.id]) : null;
                const submittedAtDate = parseDateSafely(task.submittedAt);
                const completedAtDate = parseDateSafely(task.completedAt);
                const revisionRequestedAtDate = parseDateSafely(task.revisionRequestedAt);
                const dueDate = parseDateSafely(task.endDate);

                if (!['Completed', 'Cancelled'].includes(task.status)) {
                     if (filterType === 'maximum' || isDateInRange(createdAtDate, rangeStart, rangeEnd)) {
                        stats.activeTasks++;
                    }
                }
                if (task.status === 'Submitted' && isDateInRange(submittedAtDate, rangeStart, rangeEnd)) {
                    stats.submittedInRange++;
                } else if (['Completed', 'Needs Revision'].includes(task.status) && isDateInRange(submittedAtDate, rangeStart, rangeEnd)) {
                     stats.submittedInRange++;
                }
                if (task.status === 'Completed' && isDateInRange(completedAtDate, rangeStart, rangeEnd)) {
                    stats.completedInRange++;
                }
                if (task.status === 'Needs Revision' && isDateInRange(revisionRequestedAtDate, rangeStart, rangeEnd)) {
                    stats.needsRevisionInRange++;
                }
                if (isTaskOverdueForTrainee(task) && isDateInRange(dueDate, rangeStart, rangeEnd)) {
                    stats.overdueInRange++;
                }
                if (createdAtDate && acknowledgedAtDate && isDateInRange(acknowledgedAtDate, rangeStart, rangeEnd)) {
                    const diff = acknowledgedAtDate.getTime() - createdAtDate.getTime();
                    if (diff >= 0) {
                        stats.totalAckTime += diff;
                        stats.ackTasksCount++;
                    }
                }
                if (acknowledgedAtDate && submittedAtDate && isDateInRange(submittedAtDate, rangeStart, rangeEnd)) {
                    const diff = submittedAtDate.getTime() - acknowledgedAtDate.getTime();
                    if (diff >= 0) {
                        stats.totalWorkCycleTime += diff;
                        stats.submittedTasksForCycleCount++;
                    }
                }
            });

            stats.avgAckTime = stats.ackTasksCount > 0 ? stats.totalAckTime / stats.ackTasksCount : null;
            stats.avgWorkCycleTime = stats.submittedTasksForCycleCount > 0 ? stats.totalWorkCycleTime / stats.submittedTasksForCycleCount : null;
            
            // Calculate Performance Score
            stats.performanceScore = (stats.completedInRange * 10) +
                                     (stats.submittedInRange * 3) +
                                     (stats.activeTasks * 1) -
                                     (stats.overdueInRange * 5) -
                                     (stats.needsRevisionInRange * 4);
            // Lower average times are better, so add points if they are good (e.g., below a threshold or use inverse)
            // For simplicity, we'll stick to count-based score for now, but you could add:
            // if (stats.avgAckTime !== null && stats.ackTasksCount > 0) score += (SOME_MAX_ACK_TIME - stats.avgAckTime) * ACK_TIME_WEIGHT;
            // if (stats.avgWorkCycleTime !== null && stats.submittedTasksForCycleCount > 0) score += (SOME_MAX_CYCLE_TIME - stats.avgWorkCycleTime) * CYCLE_TIME_WEIGHT;


            delete stats.totalAckTime;
            delete stats.totalWorkCycleTime;

            return {
                traineeId: trainee.id,
                traineeName: trainee.name,
                stats
            };
        }).sort((a,b) => b.stats.performanceScore - a.stats.performanceScore); // Sort by score descending
    }, [allTasks, allTrainees, achievementDateRange, achievementsDateFilter, loadingData, authLoading]);

    const todayForDateInput = useMemo(() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }, []);

    const topPerformer = useMemo(() => {
        return traineePerformanceStats.length > 0 ? traineePerformanceStats[0] : null;
    }, [traineePerformanceStats]);

    const needsImprovement = useMemo(() => {
        // Could be the last one, or if multiple have lowest score, list them or pick one.
        // For simplicity, pick the last one if more than one trainee.
        return traineePerformanceStats.length > 1 ? traineePerformanceStats[traineePerformanceStats.length - 1] : null;
    }, [traineePerformanceStats]);


    if (authLoading || loadingData) { 
        return <div className={styles.loadingIndicator}>Loading Team Performance Data...</div>; 
    }
    if (error) {
        return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
    }
    if (!userRole || !managerAccessRoles.includes(userRole)) {
         return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>Access Denied. This page is for managers only.</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.pageTitle}>Trainee Performance Report</h2>

            <div className={styles.filtersContainer}>
                 <label htmlFor="achievements-date-filter" className={styles.dateFilterLabel}>Stats For:</label>
                <select
                    id="achievements-date-filter"
                    value={achievementsDateFilter}
                    onChange={(e) => setAchievementsDateFilter(e.target.value)}
                    className={styles.filterSelect}
                >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="last28">Last 28 Days</option>
                    <option value="custom">Custom Range</option>
                    <option value="maximum">All Time</option>
                </select>
                {achievementsDateFilter === 'custom' && (
                    <div className={styles.customDateInputs}>
                        <input
                            type="date"
                            aria-label="Custom start date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className={styles.dateInput}
                            max={customEndDate || todayForDateInput}
                        />
                        <span>to</span>
                        <input
                            type="date"
                            aria-label="Custom end date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className={styles.dateInput}
                            min={customStartDate || undefined}
                            max={todayForDateInput}
                        />
                    </div>
                )}
            </div>
            {achievementsDateFilter === 'custom' && !achievementDateRange.isValid && (!customStartDate || !customEndDate) && (
                <p className={styles.customDateInfo}>Please select both start and end dates for the custom range.</p>
            )}
            {achievementsDateFilter === 'custom' && !achievementDateRange.isValid && customStartDate && customEndDate && (
                <p className={styles.customDateError}>End date must be on or after start date.</p>
            )}

            {/* Performance Summary Section */}
            {traineePerformanceStats.length > 0 && (
                <div className={styles.performanceHighlights}>
                    {topPerformer && (
                        <p className={styles.highlightItem}>
                            <strong>Top Performer:</strong> {topPerformer.traineeName} 
                            (Score: {topPerformer.stats.performanceScore.toFixed(0)})
                        </p>
                    )}
                    {needsImprovement && topPerformer?.traineeId !== needsImprovement.traineeId && (
                        <p className={styles.highlightItem}>
                            <strong>Needs Most Improvement:</strong> {needsImprovement.traineeName} 
                            (Score: {needsImprovement.stats.performanceScore.toFixed(0)})
                        </p>
                    )}
                     {traineePerformanceStats.length === 1 && topPerformer && (
                        <p className={styles.highlightItem}>
                           Only one trainee with data for this period.
                        </p>
                    )}
                </div>
            )}


            <div className={styles.employeeStatsGrid}>
                {traineePerformanceStats.length === 0 && !loadingData && 
                    <p className={styles.noDataMessage}>No trainee performance data to display for the selected period or filters.</p>
                }
                {traineePerformanceStats.map(({ traineeId, traineeName, stats }) => (
                    <div 
                        key={traineeId} 
                        className={`${styles.employeeCard} 
                                    ${topPerformer?.traineeId === traineeId ? styles.topPerformerCard : ''} 
                                    ${needsImprovement?.traineeId === traineeId && topPerformer?.traineeId !== needsImprovement.traineeId ? styles.needsImprovementCard : ''}`}
                    >
                        <h3 className={styles.employeeName}>{traineeName} 
                            <span className={styles.performanceScore}>(Score: {stats.performanceScore.toFixed(0)})</span>
                        </h3>
                        <div className={styles.statsGrid}>
                            <div className={styles.statItem}><strong>Active Tasks:</strong> {stats.activeTasks}</div>
                            <div className={styles.statItem}><strong>Submitted:</strong> {stats.submittedInRange}</div>
                            <div className={styles.statItem}><strong>Completed:</strong> {stats.completedInRange}</div>
                            <div className={`${styles.statItem} ${stats.needsRevisionInRange > 0 ? styles.statWarning : ''}`}>
                                <strong>Needs Revision:</strong> {stats.needsRevisionInRange}
                            </div>
                            <div className={`${styles.statItem} ${stats.overdueInRange > 0 ? styles.statLate : ''}`}>
                                <strong>Overdue:</strong> {stats.overdueInRange}
                            </div>
                            <div className={styles.statItem}>
                                <strong>Avg. Ack. Time:</strong> {formatAverageDuration(stats.avgAckTime)}
                                {stats.ackTasksCount > 0 && <small> ({stats.ackTasksCount} tasks)</small>}
                            </div>
                            <div className={styles.statItem}>
                                <strong>Avg. Work Cycle:</strong> {formatAverageDuration(stats.avgWorkCycleTime)}
                                {stats.submittedTasksForCycleCount > 0 && <small> (Ack to Submit, {stats.submittedTasksForCycleCount} tasks)</small>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TraineePerformanceReportPage;